import React, { useState, useEffect } from "react";
import { Search, Calendar, User, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Article, articles, fetchBlogArticles } from "@/data/blog-articles";
import { useApiClient } from "@/hooks/use-api-client";

const BlogList = () => {
  const apiClient = useApiClient();
  const [allArticles, setAllArticles] = useState<Article[]>(articles);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetchBlogArticles(apiClient)
      .then((data) => {
        if (isMounted && data && data.length > 0) {
          setAllArticles(data);
        }
      })
      .catch((err) => {
        console.error("Error loading blog articles from bulletins API:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [apiClient]);

  // Dynamically compute available categories
  const categories = Array.from(new Set(allArticles.map((a) => a.category).filter(Boolean)));

  const filteredArticles = allArticles.filter((article) => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog de Perspectivas Legales",
    description: "Explora artículos sobre tecnología legal, IA en derecho colombiano, y las últimas tendencias en servicios legales.",
    url: "https://miasistentelegalia.com/blog",
    blogPost: filteredArticles.map((article) => ({
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      author: {
        "@type": "Person",
        name: article.author,
      },
      datePublished: article.date,
      image: article.image,
      url: `https://miasistentelegalia.com/blog/${article.id}`,
    })),
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Blog de Perspectivas Legales | Legal AI Colombia"
        description="Explora artículos sobre tecnología legal, IA en derecho colombiano, Sentencia T-323/24 y las últimas tendencias en servicios jurídicos automatizados."
        keywords="blog legal colombia, ia en derecho, sentencia t-323/24, tecnologia legal, derecho laboral colombia, dosimetria penal, samai cpnu tyba"
        type="blog"
      />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <Header />
      <main className="flex-grow">
        {/* Header */}
        <section className="bg-legal-navy text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 bg-legal-gold/20 text-legal-gold font-semibold rounded-full text-xs uppercase tracking-wider mb-4">
                Boletines & Artículos Actualizados
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Blog de Perspectivas Legales e IA
              </h1>
              <p className="text-xl mb-8 opacity-90">
                Alimentado en tiempo real desde el módulo de boletines con el mejor análisis del Derecho Colombiano
              </p>
              <div className="relative max-w-lg mx-auto">
                <Input
                  type="text"
                  placeholder="Buscar artículos por título o palabra clave..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/60 pl-10 rounded-full h-12 text-base focus:ring-2 focus:ring-legal-gold"
                />
                <Search
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-white/60"
                  size={18}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                variant={!selectedCategory ? "default" : "outline"}
                onClick={() => setSelectedCategory(null)}
                className="rounded-full px-5 text-sm">
                Todos ({allArticles.length})
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                  className="rounded-full px-4 text-sm">
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-10 w-10 animate-spin text-legal-blue mb-4" />
                <p className="text-gray-500 font-medium">Cargando boletines del blog...</p>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-12 max-w-md mx-auto">
                <h2 className="text-2xl font-bold text-legal-navy mb-3">
                  No se encontraron artículos
                </h2>
                <p className="text-gray-600 mb-6">
                  Intenta con otros términos de búsqueda o selecciona una categoría diferente.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory(null);
                  }}
                  className="rounded-full">
                  Limpiar filtros
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article) => (
                  <article
                    key={article.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex flex-col transition-all duration-200 hover:-translate-y-1.5 hover:shadow-xl">
                    <div className="h-52 overflow-hidden relative">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 bg-legal-navy/80 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                    </div>
                    <div className="p-6 flex-grow flex flex-col">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <div className="flex items-center">
                          <Calendar size={13} className="mr-1 text-legal-blue" />
                          <span>{article.date}</span>
                        </div>
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600 font-medium">
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="font-bold text-xl mb-3 text-legal-navy line-clamp-2 hover:text-legal-blue transition-colors">
                        <Link to={`/blog/${article.id}`}>{article.title}</Link>
                      </h3>
                      <p className="text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed flex-grow">
                        {article.excerpt}
                      </p>
                      <div className="pt-4 border-t border-gray-100 flex items-center justify-between mt-auto">
                        <div className="flex items-center text-xs text-gray-500 font-medium">
                          <User size={13} className="mr-1 text-gray-400" />
                          <span className="truncate max-w-[130px]">{article.author}</span>
                        </div>
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="text-legal-blue hover:text-legal-navy p-0 hover:bg-transparent font-semibold text-xs group">
                          <Link to={`/blog/${article.id}`} className="flex items-center gap-1">
                            Leer Más
                            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogList;
